import type { TokenCredential } from "@azure/identity";
import { mintInstallationToken } from "@m8t-stack/github-app-auth";
import { parse as parseYaml } from "yaml";
import { LocalCliError } from "./errors.js";

const DEFAULT_AUTHORITATIVE = [
  `# ── AUTHORITATIVE — engine + process config. The repo IS the source of truth`,
  `# for this section. Tooling must merge-preserve it, never regenerate it.`,
  `engine:`,
  `  version: "0.1.0"`,
  `processes:`,
  `  dreamer:`,
  `    enabled: true`,
  `    cadence: nightly          # skip-if-no-new pre-check is built into the dreamer`,
  `  librarian:`,
  `    enabled: true`,
  `    lifecycle: { inbox: 14d, stale: 30d, archive: 90d }`,
  `    artifactRetention: 30d`,
  `    digestRetention: 90d`,
  `  costReport:`,
  `    enabled: true`,
  `    cadenceDays: 14`,
  `  teaching:`,
  `    enabled: false              # operator opt-in; gateway flag and teacher capability are also required`,
].join("\n");

const MIRROR_BANNER = [
  `# ── MIRROR — link record convenience copy. NOT the source of truth —`,
  `# the Foundry agent's metadata.brain is. Rewritten by \`m8t brain link\`.`,
].join("\n");

function linkSection(args: { repo: string; branch: string; persona: string; agents: string[] }): string {
  return [
    `link:`,
    `  repo: "${args.repo}"`,
    `  branch: ${args.branch}`,
    `  topology: per-worker`,
    `  schemaVersion: "2"`,
    `  persona: "${args.persona}"`,
    `  agents:`,
    ...args.agents.map((a) => `    - ${a}`),
    ``,
  ].join("\n");
}

/** Merge an incoming agent into the prior set, preserving first-seen order and
 *  deduping. Idempotent: re-linking an already-present agent is a no-op. */
export function mergeAgents(prior: string[], incoming: string): string[] {
  return prior.includes(incoming) ? prior : [...prior, incoming];
}

/** Read the set of agents recorded in an existing .m8t/brain.yaml mirror.
 *  Accepts the new `link.agents` list, the legacy scalar `link.agent`, and the
 *  legacy-flat shape (keys at the top level). Ignores empty/`{{placeholder}}`
 *  values and returns [] for a missing or unparseable file. */
export function readLinkAgents(existing: string | undefined): string[] {
  if (!existing) return [];
  let parsed: unknown;
  try {
    parsed = parseYaml(existing);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== "object") return [];
  const root = parsed as Record<string, unknown>;
  const link = (root.link && typeof root.link === "object" ? root.link : root) as Record<string, unknown>;
  const raw: unknown[] = Array.isArray(link.agents)
    ? link.agents
    : typeof link.agent === "string"
      ? [link.agent]
      : [];
  const out: string[] = [];
  for (const v of raw) {
    const s = typeof v === "string" ? v.trim() : "";
    if (!s) continue;
    if (/^\{\{.*\}\}$/.test(s)) continue; // unfilled template placeholder
    if (!out.includes(s)) out.push(s);
  }
  return out;
}

/** Build the split .m8t/brain.yaml. The AUTHORITATIVE region of an existing
 *  split file is preserved BYTE-FOR-BYTE (operator-owned); only the
 *  link mirror is regenerated. Legacy flat files and missing files get the
 *  default authoritative section. Exported for unit tests. */
export function buildBrainYaml(
  existing: string | undefined,
  args: { repo: string; branch: string; persona: string; agent: string },
): string {
  const agents = mergeAgents(readLinkAgents(existing), args.agent);
  const link = linkSection({ repo: args.repo, branch: args.branch, persona: args.persona, agents });
  if (existing) {
    const hasAuthoritative = /^engine:/m.test(existing) || /^processes:/m.test(existing);
    if (hasAuthoritative) {
      const mirrorIdx = existing.search(/^# ── MIRROR/m);
      const linkIdx = existing.search(/^link:/m);
      const cut = mirrorIdx >= 0 ? mirrorIdx : linkIdx >= 0 ? linkIdx : existing.length;
      const authoritative = existing.slice(0, cut).replace(/\s*$/, "\n\n");
      return authoritative + MIRROR_BANNER + "\n" + link;
    }
  }
  return DEFAULT_AUTHORITATIVE + "\n\n" + MIRROR_BANNER + "\n" + link;
}

export async function mirrorBrainYaml(args: {
  credential: TokenCredential;
  kvUri: string;
  installationId: string;
  repo: string;
  branch: string;
  persona: string;
  agent: string;
}): Promise<void> {
  const minted = await mintInstallationToken({
    credential: args.credential,
    kvUri: args.kvUri,
    installationId: args.installationId,
    repository: args.repo,
  });
  const [owner, repoName] = args.repo.split("/");
  const url = `https://api.github.com/repos/${owner}/${repoName}/contents/.m8t/brain.yaml`;
  const headers = {
    Authorization: `Bearer ${minted.token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
  };

  // A concurrent linker writing the same brain.yaml causes a 409 (sha mismatch).
  // Re-GET (fresh sha + content) and re-run buildBrainYaml, which re-merges the
  // now-current agents — so the retry converges to the UNION rather than racing.
  const MAX_ATTEMPTS = 3;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const getRes = await fetch(url, { headers });
    let sha: string | undefined;
    let existing: string | undefined;
    if (getRes.ok) {
      const data = (await getRes.json()) as { sha?: string; content?: string };
      sha = data.sha;
      if (typeof data.content === "string") {
        existing = Buffer.from(data.content, "base64").toString("utf8");
      }
    }
    const content = buildBrainYaml(existing, {
      repo: args.repo,
      branch: args.branch,
      persona: args.persona,
      agent: args.agent,
    });
    const body = {
      message: "chore(brain): mirror .m8t/brain.yaml (m8t brain link)",
      content: Buffer.from(content, "utf8").toString("base64"),
      branch: args.branch,
      ...(sha ? { sha } : {}),
    };
    const putRes = await fetch(url, {
      method: "PUT",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (putRes.ok) return;
    const text = await putRes.text();
    // 409 Conflict (or 422) == raced by another linker. Re-GET and retry until
    // the attempt budget is spent; then throw with the exhaustion message.
    const retryable = putRes.status === 409 || putRes.status === 422;
    if (retryable && attempt < MAX_ATTEMPTS) continue;
    const suffix = retryable ? ` after ${MAX_ATTEMPTS.toString()} attempts` : "";
    throw new LocalCliError({
      code: "BRAIN_YAML_MIRROR_FAILED",
      message: `PUT .m8t/brain.yaml: HTTP ${putRes.status.toString()}${suffix}\n${text.slice(0, 300)}`,
    });
  }
  // Unreachable — the loop always returns on success or throws on the final
  // attempt — but satisfies the compiler's no-implicit-return.
  throw new LocalCliError({
    code: "BRAIN_YAML_MIRROR_FAILED",
    message: `PUT .m8t/brain.yaml: retry loop exited unexpectedly`,
  });
}
